import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useDeleteFee } from '../../lib/resources/mutations';
import { parseError } from '../../lib/api/client';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import type { FeeConfigResponse } from '../../lib/resources/types';

interface FeeDeleteConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fee: FeeConfigResponse | null;
}

export function FeeDeleteConfirmDialog({ open, onOpenChange, fee }: FeeDeleteConfirmDialogProps) {
  const del = useDeleteFee();
  const [serverError, setServerError] = useState<string | null>(null);

  async function onConfirm() {
    if (!fee) return;
    setServerError(null);
    try {
      await del.mutateAsync(fee.id);
      onOpenChange(false);
    } catch (e) {
      const err = await parseError(e);
      setServerError(err.message || 'Delete failed');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Delete fee — {fee?.name}</DialogTitle>
          <DialogDescription>
            Permanently removes this fee. Already-finalized master invoices keep their
            snapshot of the fee at the time they were finalized; only future master
            invoices are affected. To temporarily disable instead, use Toggle.
          </DialogDescription>
        </DialogHeader>

        {serverError && (
          <div className="text-[12px] text-error" role="alert">{serverError}</div>
        )}

        <DialogFooter>
          <Button variant="secondary" type="button" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" type="button" disabled={del.isPending} onClick={onConfirm}>
            {del.isPending && <Loader2 className="size-3.5 animate-spin" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
